// Bible reader Study Mode data layer. Lazy-loaded glossary lookup +
// phrase-aware tokenizer that wraps lookupable words in glowing spans
// for tap-to-define.
//
// Three artifacts shipped under /glossary/ (built by
// scripts/glossary/build-public-data.mjs from the operator-curated
// masters in scripts/glossary/{glossary,locations}-master.json +
// per-book markdown in scripts/glossary/output/{vocab,locations}/):
//
//   - headwords.json          — source-qualified entries keyed by
//                                "<source>:<rawHeadword>" entry IDs;
//                                runtime definitions live here.
//   - manifest.json           — { books, headwordCount, ... } telling
//                                the reader which books have curated
//                                occurrences (avoids 404 spam on
//                                uncurated books).
//   - occurrences/<slug>.json — per-book per-verse map of
//                                normalizedKey → entryId. Only verses
//                                the curator flagged appear here; the
//                                reader uses this as the structural
//                                gate against cross-book and
//                                cross-verse false positives. (Note:
//                                same-token-twice-in-verse still
//                                glows both instances — token-offset
//                                pinning is a future hardening step.)
//
// MIRROR NOTICE: makeEntryId / parseEntryId / normalizeKey /
// tokenizeForGlossary are also implemented in
// scripts/glossary/build-public-data.mjs. Keep both in lockstep —
// the build script needs them at build time, the reader needs them
// at runtime, and Vite cannot import .mjs build scripts. CLAUDE.md
// rule #4 cross-boundary-duplication sub-rule applies.
// (shouldSkipHeadword is BUILD-ONLY by design: the build filters
// skip-pattern master keys before emitting headwords.json, so the
// runtime never sees them and carries no copy.)
//
// After changing either side, run
// `npx tsx scripts/glossary/test-tokenizer-parity.mjs` — it imports
// BOTH real implementations and compares behavior across the
// fixture corpus, so drift on either side fails the test.

export type GlossarySource = 'vocab' | 'location';

export interface GlossaryEntry {
  /** Original headword from the master file, preserving casing for display. */
  headword: string;
  definition: string;
  source: GlossarySource;
}

export interface GlossaryManifest {
  books: Set<string>;
  headwordCount: number;
  occurrenceCount: number;
}

/** Per-verse map: normalized phrase key → source-qualified entry ID. */
export type PerVerseMap = Map<string, string>;

/** Per-book occurrence map: "chapter:verse" → per-verse map. */
export type PerBookOccurrences = Map<string, PerVerseMap>;

export type GlossarySegment =
  | { kind: 'plain'; text: string }
  | { kind: 'glow'; text: string; entryId: string };

// ─────────────────────────────────────────────────────────────────
// Entry ID helpers (mirrored in build-public-data.mjs)
// ─────────────────────────────────────────────────────────────────

export function makeEntryId(source: GlossarySource, rawHeadword: string): string {
  return `${source}:${rawHeadword}`;
}

/**
 * Parse an entry ID into its source + raw headword. Validates that the
 * source token is exactly 'vocab' or 'location' rather than trusting
 * an `as` cast — defensive against future drift if the build script
 * or markdown produces an unknown source string.
 */
export function parseEntryId(entryId: string): {
  source: GlossarySource;
  rawHeadword: string;
} {
  const idx = entryId.indexOf(':');
  if (idx < 0) throw new Error(`malformed entryId (no colon): ${entryId}`);
  const sourceStr = entryId.slice(0, idx);
  if (sourceStr !== 'vocab' && sourceStr !== 'location') {
    throw new Error(
      `malformed entryId (unknown source "${sourceStr}"): ${entryId}`,
    );
  }
  const rawHeadword = entryId.slice(idx + 1);
  if (!rawHeadword) {
    throw new Error(`malformed entryId (empty headword): ${entryId}`);
  }
  return { source: sourceStr, rawHeadword };
}

// ─────────────────────────────────────────────────────────────────
// Normalization
// ─────────────────────────────────────────────────────────────────

/**
 * Lowercase, strip parenthetical disambiguators ("Achzib (Asher)" →
 * "achzib"), strip leading/trailing punctuation, collapse whitespace.
 * Curly apostrophes (U+2019, the BSB rendering) are normalized to the
 * straight ASCII apostrophe FIRST so a straight-apostrophe headword
 * ("ram's horn") matches a curly-apostrophe verse ("ram’s horn").
 * Internal apostrophes are kept so "don't" matches its entry.
 * Internal hyphens are kept so "Beth-shemesh" normalizes correctly.
 * A TRAILING possessive ("'s" or "'") is stripped LAST so a base
 * headword ("Caesar", "Israelites") still matches a possessive verse
 * form ("Caesar's", "Israelites'") now that the apostrophe is a word
 * char. Mid-phrase possessives ("ram's horn") are untouched.
 *
 * MUST stay byte-identical to normalizeKey in
 * scripts/glossary/build-public-data.mjs.
 */
export function normalizeKey(s: string): string {
  return s
    .replace(/’/g, "'")
    .toLowerCase()
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/^[^a-z0-9']+/, '')
    .replace(/[^a-z0-9']+$/, '')
    .replace(/\s+/g, ' ')
    .replace(/'s?$/, '')
    .trim();
}

function countWords(phrase: string): number {
  if (!phrase) return 0;
  return phrase.split(/\s+/).length;
}

// ─────────────────────────────────────────────────────────────────
// Headwords (global definition lookup)
// ─────────────────────────────────────────────────────────────────

let cachedHeadwords: Promise<Map<string, GlossaryEntry>> | null = null;

/**
 * Load the global headword map. Cached after first successful resolve.
 * On failure the cache is cleared so the next call retries.
 */
export function loadHeadwords(): Promise<Map<string, GlossaryEntry>> {
  if (cachedHeadwords) return cachedHeadwords;
  cachedHeadwords = (async () => {
    try {
      const res = await fetch('/glossary/headwords.json', { cache: 'reload' });
      if (!res.ok) {
        throw new Error(`glossary headwords fetch failed: ${res.status}`);
      }
      const raw = (await res.json()) as Record<
        string,
        { headword: string; definition: string; source: GlossarySource }
      >;
      const map = new Map<string, GlossaryEntry>();
      for (const [entryId, entry] of Object.entries(raw)) {
        map.set(entryId, {
          headword: entry.headword,
          definition: entry.definition,
          source: entry.source,
        });
      }
      return map;
    } catch (err) {
      cachedHeadwords = null;
      throw err;
    }
  })();
  return cachedHeadwords;
}

// ─────────────────────────────────────────────────────────────────
// Manifest (curated-book index)
// ─────────────────────────────────────────────────────────────────

let cachedManifest: Promise<GlossaryManifest> | null = null;

export function loadManifest(): Promise<GlossaryManifest> {
  if (cachedManifest) return cachedManifest;
  cachedManifest = (async () => {
    try {
      const res = await fetch('/glossary/manifest.json', { cache: 'reload' });
      if (!res.ok) {
        throw new Error(`glossary manifest fetch failed: ${res.status}`);
      }
      const raw = (await res.json()) as {
        books: string[];
        headwordCount: number;
        occurrenceCount: number;
      };
      return {
        books: new Set(raw.books),
        headwordCount: raw.headwordCount,
        occurrenceCount: raw.occurrenceCount,
      };
    } catch (err) {
      cachedManifest = null;
      throw err;
    }
  })();
  return cachedManifest;
}

// ─────────────────────────────────────────────────────────────────
// Per-book occurrences
// ─────────────────────────────────────────────────────────────────

const occurrencesCache = new Map<string, Promise<PerBookOccurrences>>();

/**
 * Lazy-load a curated book's occurrence map. Only successful loads are cached.
 * The manifest authorizes coverage: uncurated books return an empty map without
 * a request; a missing curated artifact is an error, never cached as no coverage.
 */
export function loadOccurrencesForBook(
  bookSlug: string,
): Promise<PerBookOccurrences> {
  const cached = occurrencesCache.get(bookSlug);
  if (cached) return cached;
  const promise = (async () => {
    try {
      const manifest = await loadManifest();
      if (!manifest.books.has(bookSlug)) return new Map<string, PerVerseMap>();
      // Also bypass a stale HTTP/SW response when retrying a failed artifact.
      // public/sw.js honors reload only for glossary URLs, with offline fallback.
      const res = await fetch(`/glossary/occurrences/${bookSlug}.json`, { cache: 'reload' });
      if (!res.ok) {
        throw new Error(
          `glossary occurrences fetch failed for ${bookSlug}: ${res.status}`,
        );
      }
      const raw: unknown = await res.json();
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        throw new Error(`invalid glossary occurrences for ${bookSlug}`);
      }
      const out: PerBookOccurrences = new Map();
      for (const [verseKey, nkMap] of Object.entries(raw)) {
        if (!/^\d+:\d+$/.test(verseKey) || !nkMap || typeof nkMap !== 'object' || Array.isArray(nkMap)) {
          throw new Error(`invalid glossary occurrence verse for ${bookSlug}`);
        }
        const inner: PerVerseMap = new Map();
        for (const [nk, entryId] of Object.entries(nkMap)) {
          if (!nk || typeof entryId !== 'string') {
            throw new Error(`invalid glossary occurrence entry for ${bookSlug}`);
          }
          parseEntryId(entryId);
          inner.set(nk, entryId);
        }
        out.set(verseKey, inner);
      }
      return out;
    } catch (err) {
      occurrencesCache.delete(bookSlug);
      throw err;
    }
  })();
  occurrencesCache.set(bookSlug, promise);
  return promise;
}

// ─────────────────────────────────────────────────────────────────
// Tokenizer
// ─────────────────────────────────────────────────────────────────

/**
 * Tokenize a verse text string into renderable segments. Returns plain
 * segments verbatim (whitespace + punctuation preserved) and `glow`
 * segments for matched headwords/phrases.
 *
 * The `allowedForVerse` map gates matching — only normalized keys
 * present in this map can glow, and the map's value (the source-
 * qualified entryId) tells the caller which headwords.json entry to
 * render in the definition sheet.
 *
 * Phrase matching is greedy longest-first per word position, so "be
 * fruitful and multiply" wins over individual words when both forms
 * are in the allowed map.
 *
 * Tokenizer treats ASCII hyphen-minus (U+002D) as a word character
 * so "Beth-shemesh" tokenizes as a single word matching the
 * "Beth-shemesh" headword. En-dash (U+2013) and em-dash (U+2014) stay
 * as non-word separators so "He said—Behold" tokenizes correctly.
 */
export function tokenizeForGlossary(
  text: string,
  allowedForVerse: PerVerseMap,
  maxPhraseLength: number = 1,
): GlossarySegment[] {
  if (!text) return [];
  if (allowedForVerse.size === 0) return [{ kind: 'plain', text }];

  // Split into alternating word / non-word tokens. Apostrophes (straight
  // and curly U+2019) + ASCII hyphen-minus count as part of a word so
  // "don't", "ram’s", and "Beth-shemesh" each tokenize as one word.
  const tokens: { value: string; isWord: boolean }[] = [];
  const re = /([a-zA-Z'’-]+)|([^a-zA-Z'’-]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    tokens.push({ value: m[1] ?? m[2], isWord: m[1] != null });
  }

  const segments: GlossarySegment[] = [];
  let buffer = '';

  const flushBuffer = () => {
    if (buffer) {
      segments.push({ kind: 'plain', text: buffer });
      buffer = '';
    }
  };

  // Effective phrase ceiling = the max of (declared max) and (the
  // max actually allowed in this verse). The verse map drives the
  // bound; max-from-headwords is a safety floor.
  const effectiveMax = (() => {
    let m = maxPhraseLength;
    for (const key of allowedForVerse.keys()) {
      const wc = countWords(key);
      if (wc > m) m = wc;
    }
    return m;
  })();

  let i = 0;
  while (i < tokens.length) {
    const tok = tokens[i];
    if (!tok.isWord) {
      buffer += tok.value;
      i++;
      continue;
    }

    // Try phrase lengths from longest down to 1 starting at this word.
    let matched: { endIdx: number; entryId: string; matchText: string } | null = null;

    for (let phraseLen = effectiveMax; phraseLen >= 1; phraseLen--) {
      let wordsSeen = 0;
      let j = i;
      while (j < tokens.length && wordsSeen < phraseLen) {
        if (tokens[j].isWord) wordsSeen++;
        j++;
      }
      // Roll back any trailing non-word tokens pulled in by the loop —
      // they belong to the next segment, not this match.
      while (j > i && !tokens[j - 1].isWord) j--;
      let matchText = '';
      for (let k = i; k < j; k++) matchText += tokens[k].value;

      if (wordsSeen < phraseLen) continue;
      const key = normalizeKey(matchText);
      if (!key) continue;
      const entryId = allowedForVerse.get(key);
      if (entryId) {
        matched = { endIdx: j, entryId, matchText };
        break;
      }
    }

    if (matched) {
      flushBuffer();
      segments.push({ kind: 'glow', text: matched.matchText, entryId: matched.entryId });
      i = matched.endIdx;
    } else {
      buffer += tok.value;
      i++;
    }
  }

  flushBuffer();
  return segments;
}
